import { VectorImageName } from '~components/media/vectorImage';
import { UserRoleEnum } from '~enums';

/**
 * The kinds of entries the side menu can render.
 *
 * `type` is the discriminant: `Section` and `Dropdown` are otherwise
 * structurally similar, so an explicit tag is required for the union to narrow
 * correctly (and for the `switch` in {@link SideMenuItem}).
 */
export enum SideMenuItemType {
	Page = 'page',
	Section = 'section',
	Dropdown = 'dropdown',
}

/**
 * A single, clickable navigation entry (a leaf).
 *
 * The component is intentionally "dumb": `label` is an already-localized
 * string, so the menu never has to know anything about i18n.
 */
export interface SideMenuPage {
	type: SideMenuItemType.Page;
	/** Already-localized, ready-to-render label. */
	label: string;
	path: string;
	iconName?: VectorImageName;
	/** Optional badge value, e.g. `3` pending work tasks. */
	badge?: number;
	roles?: UserRoleEnum[];
}

/** A clickable title that expands/collapses a set of pages. */
export interface SideMenuDropdown {
	type: SideMenuItemType.Dropdown;
	/** Already-localized title. */
	title: string;
	/** Shown on the collapsed (icon-only) rail and next to the title. */
	iconName?: VectorImageName;
	pages: SideMenuPage[];
	roles?: UserRoleEnum[];
}

/**
 * A static, non-clickable group header. Its children can be plain pages and/or
 * dropdowns (e.g. "Operations" holding the "Manufacturing" dropdown next to the
 * "Service tickets" page).
 */
export interface SideMenuSection {
	type: SideMenuItemType.Section;
	/** Already-localized title. */
	title: string;
	items: (SideMenuPage | SideMenuDropdown)[];
	roles?: UserRoleEnum[];
}

export type SideMenuEntry = SideMenuPage | SideMenuSection | SideMenuDropdown;
